// Keys whose values are redacted before a payload is sent to third-party analytics/monitoring
// (Segment, Bugsnag). Covers auth secrets, user PII, address, and health content (e.g. logged
// meals) so PII/PHI isn't shared via event properties or breadcrumbs.
//
// This is only applied to operational/effect telemetry (the effect logger in `model.ts` and the
// `Logger` events) — NOT to product `Analytics.track` metrics — so redacting broad keys like
// `name`/`state`/`title` here is safe: it only trims debug payloads, it doesn't touch product
// analytics. Payloads such as `updateAddress` and edited-meal submissions spread flat fields, so
// the list is intentionally broad.
const SENSITIVE_KEYS = [
  // Auth secrets
  'password',
  'currentPassword',
  'token',
  'providerToken',
  'accessToken',
  'refreshToken',
  'idToken',
  // Personally identifiable information
  'email',
  'firstName',
  'lastName',
  'fullName',
  'name',
  'phoneNumber',
  'dateOfBirth',
  'governmentIdCard',
  // Address (updateAddress sends these as flat fields)
  'address',
  'street',
  'street2',
  'city',
  'state',
  'zipCode',
  'country',
  // Health content (PHI) — logged meals and their derived glucose/nutrition data
  'description',
  'ingredients',
  'nutrition',
  'glucose',
  'statistics',
  'title',
  'note',
  // User-authored free text (searches, support/journal content, survey answers) — can contain
  // PII/PHI. Broad, but this only trims operational telemetry, never product Analytics.track.
  'query',
  'value',
  'body',
  'message',
  'subject',
  'comment',
  'feedback',
  'answer',
  // Meal photo references
  'avatar',
  'imageUrl',
  'photo',
  'photoSignedId',
]

const FILTER_MASK = '[FILTERED]'

// For logging effect payloads to third parties (Segment/Bugsnag): return only the field names,
// never the values. Effect payloads are GraphQL mutation variables that routinely carry PII/PHI
// (meals, address, insurance, support free-text, profile updates), so a keyword denylist can't
// safely keep up — logging just the shape avoids leaking any values while still recording which
// fields an effect sent.
export const payloadKeysForLogging = (payload: unknown): string[] =>
  payload && typeof payload === 'object' ? Object.keys(payload as Record<string, unknown>) : []

export const filterSensitiveData = (
  data: Record<string, unknown> | Array<Record<string, unknown>> | undefined,
): any => {
  if (Array.isArray(data)) {
    return data.map((element) => filterSensitiveData(element))
  }

  if (data && typeof data === 'object') {
    const newData = { ...data }
    Object.keys(newData).forEach((key) => {
      if (SENSITIVE_KEYS.includes(key)) {
        // Mask regardless of value type so arrays/objects (e.g. ingredients) are redacted too.
        if (newData[key] !== undefined && newData[key] !== null) {
          newData[key] = FILTER_MASK
        }
      } else if (typeof newData[key] === 'object') {
        newData[key] = filterSensitiveData(newData[key] as any)
      }
    })
    return newData
  }

  return data
}
